import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Clock, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { useEventBySlug } from '@/hooks/useEvents';
import { formatDateBrussels, isEventDateInPast } from '@/lib/utils';
import { formatHmInBrussels } from '@/lib/brusselsSchedule';
import { formatHmForDisplayFr, normalizeEndTimeToHm } from '@/lib/eventTimes';
import { hasSpeakerPublicContent, formatPrimarySpeakerLine } from '@/lib/speaker';
import AddToCalendar from '@/components/agenda/AddToCalendar';

export default function EventDetail() {
    const { slug } = useParams();
    const { data: event, isLoading } = useEventBySlug(slug);

    const getFallbackImage = (type) => {
        switch (type) {
            case 'Conférence': return 'https://images.unsplash.com/photo-1544531586-fde5298cdd40?q=80&w=1200&auto=format&fit=crop';
            case 'Excursion': return 'https://images.unsplash.com/photo-1501555088652-021faa106b9b?q=80&w=1200&auto=format&fit=crop';
            case 'Goûter': return 'https://images.unsplash.com/photo-1559563362-c667ba5f5480?q=80&w=1200&auto=format&fit=crop';
            default: return 'https://images.unsplash.com/photo-1544531586-fde5298cdd40?q=80&w=1200&auto=format&fit=crop';
        }
    };

    const getEventImage = (evt) => {
        if (evt?.slug === 'excursion-maastricht-2026') {
            return '/images/excursion-maastricht-2026.webp';
        }
        return evt?.image_url || getFallbackImage(evt?.type);
    };

    // Hooks must stay unconditional across renders to avoid React invariant errors.
    const [heroImgSrc, setHeroImgSrc] = useState(() => getEventImage(event));

    useEffect(() => {
        setHeroImgSrc(getEventImage(event));
    }, [event?.slug, event?.image_url, event?.type]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!event) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Événement introuvable</h1>
                <Link to="/agenda" className="text-primary-600 hover:underline">
                    Retour à l'agenda
                </Link>
            </div>
        );
    }

    const eventIsPast = event?.date ? isEventDateInPast(event.date) : false;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-white min-h-screen pb-20"
        >
            {/* Hero Header */}
            <div className={`relative ${eventIsPast ? 'py-20 bg-primary-900' : 'h-[60vh] min-h-[400px]'}`}>
                {!eventIsPast && (
                    <>
                        <img
                            src={heroImgSrc}
                            alt={event.title}
                            onError={() => setHeroImgSrc(getFallbackImage(event.type))}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    </>
                )}

                <div className={`${eventIsPast ? 'relative' : 'absolute bottom-0 left-0'} w-full p-6 md:p-12 text-white`}>
                    <div className="container mx-auto">
                        <Link to="/agenda" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
                            <ArrowLeft size={20} />
                            Retour à l'agenda
                        </Link>

                        <span className="block text-accent-400 font-bold uppercase tracking-wider mb-2">
                            {event.type}
                        </span>
                        <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6 max-w-4xl leading-tight">
                            {event.title}
                        </h1>

                        {/* Quick Info Bar */}
                        <div className="flex flex-wrap gap-6 md:gap-12 text-lg font-medium text-white/90">
                            <div className="flex items-center gap-3">
                                <Calendar className="text-accent-500 shrink-0" />
                                <span>{formatDateBrussels(event.date)}</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <Clock className="text-accent-500 shrink-0 mt-0.5" />
                                <div className="flex flex-col gap-0.5 text-base">
                                    <span>Début : {formatHmForDisplayFr(formatHmInBrussels(event.date))}</span>
                                    <span>Fin : {formatHmForDisplayFr(normalizeEndTimeToHm(event.end_time))}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <MapPin className="text-accent-500 shrink-0" />
                                {event.location}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-3 gap-12">

                {/* Main Content */}
                <div className="lg:col-span-2 space-y-8">
                    <div className="prose prose-lg prose-blue max-w-none">
                        <h2 className="text-3xl font-serif font-bold text-primary-900 mb-6">À propos de l'événement</h2>
                        <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                            {event.description}
                        </p>
                    </div>

                    {hasSpeakerPublicContent(event) && (
                        <div className="bg-primary-50 p-8 rounded-2xl flex items-start gap-6">
                            <div className="bg-primary-200 p-4 rounded-full">
                                <User size={32} className="text-primary-800" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-primary-900 mb-1">Intervenant</h3>
                                {formatPrimarySpeakerLine(event) && (
                                    <p className="text-lg font-medium text-primary-700">{formatPrimarySpeakerLine(event)}</p>
                                )}
                                {event.speaker_name?.trim() && event.speaker_title?.trim() && (
                                    <p className="text-primary-600 italic text-sm mb-2">{event.speaker_title}</p>
                                )}
                                {event.speaker_description?.trim() && (
                                    <p className="text-gray-600 text-sm mt-2 border-t border-primary-100 pt-2">
                                        {event.speaker_description}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="lg:col-span-1 space-y-8">
                    <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-8 sticky top-32">
                        <h3 className="text-xl font-bold text-primary-900 mb-6">Participer</h3>

                        <div className="space-y-6">
                            <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                <span className="text-gray-600">Date</span>
                                <span className="font-medium text-gray-900 text-right">{formatDateBrussels(event.date)}</span>
                            </div>
                            <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                <span className="text-gray-600">Début</span>
                                <span className="font-medium text-gray-900">{formatHmForDisplayFr(formatHmInBrussels(event.date))}</span>
                            </div>
                            <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                <span className="text-gray-600">Fin</span>
                                <span className="font-medium text-gray-900">{formatHmForDisplayFr(normalizeEndTimeToHm(event.end_time))}</span>
                            </div>
                            <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                <span className="text-gray-600">Lieu</span>
                                <span className="font-medium text-gray-900 text-right max-w-[60%]">{event.location}</span>
                            </div>
                        </div>

                        <div className="mt-8 space-y-4">
                            <div className="w-full">
                                <AddToCalendar event={event} />
                            </div>
                            {/* Future: Registration button */}
                        </div>
                    </div>
                </div>

            </div>
        </motion.div>
    );
}
